import React from 'react';
import { Box, Typography } from '@mui/material';
import { coilShiftY } from '../../utils/coilShiftData';

export interface CoilShiftCurvePlotProps {
  data: unknown[];
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  showGrid?: boolean;
}

/** Coil shift trace — same geometry as mobile `CurvePlot` (viewBox 300×120). */
const CoilShiftCurvePlot: React.FC<CoilShiftCurvePlotProps> = ({
  data,
  height = 150,
  strokeColor = '#3B82F6',
  strokeWidth = 2,
  showGrid = true,
}) => {
  if (!data || data.length < 2) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          bgcolor: 'rgba(0,0,0,0.12)',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No curve data
        </Typography>
      </Box>
    );
  }

  const shifts = data.map((p) => coilShiftY(p));
  const maxShift = Math.max(...shifts);
  const minShift = Math.min(...shifts);
  const range = maxShift - minShift || 1;

  const plotWidth = 300;
  const plotHeight = 120;

  const points = data
    .map((p, i) => {
      const x = (i / (data.length - 1)) * plotWidth;
      const y = ((maxShift - coilShiftY(p)) / range) * plotHeight;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <Box
      sx={{
        height,
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'rgba(0,0,0,0.08)',
      }}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${plotWidth} ${plotHeight}`}
        preserveAspectRatio="none"
        aria-label="Scan curve data coil shift"
      >
        {showGrid && (
          <line
            x1="0"
            y1={plotHeight / 2}
            x2={plotWidth}
            y2={plotHeight / 2}
            stroke="rgba(100,100,120,0.25)"
            strokeWidth={1}
          />
        )}
        <polyline
          points={points}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </svg>
    </Box>
  );
};

export default CoilShiftCurvePlot;
